import { notifications } from "@mantine/notifications";
import { useEffect, useRef, useState } from "react";

import {
  AUTO_CLOSE_TOAST_DURATION,
  showV2Toast,
  showV2CompactToast,
  updateV2Toast,
} from "#app/notifications";
import { useUndoableAction } from "#modules/onboardingV2/view/useUndoableAction";

// Deletion is optimistic-with-undo: rows are only hidden locally while the toast is shown,
// and the actual delete mutation only fires once the undo window elapses without a cancel.
// Consecutive deletions are grouped into one batch/toast, and every new deletion restarts
// the undo window for the whole batch. A batch of more than one row is committed via the
// bulk-delete mutation instead of one single-delete call per row.
const BATCH_DELETE_TOAST_ID = "material-row-batch-delete";

export const useDeletableMaterialRows = <TRow>({
  rows,
  getRowId,
  getDeleteToastMessage,
  getUndoToastMessage,
  onConfirmDelete,
  onConfirmBulkDelete,
}: {
  rows: TRow[];
  getRowId: (row: TRow) => string;
  getDeleteToastMessage: (count: number) => string;
  getUndoToastMessage: (count: number) => string;
  onConfirmDelete: (row: TRow) => void;
  onConfirmBulkDelete: (rows: TRow[]) => void;
}) => {
  const [pendingDeleteIds, setPendingDeleteIds] = useState<Set<string>>(new Set());
  const pendingActions = useUndoableAction<TRow[]>();
  const batchRowsRef = useRef<TRow[] | null>(null);

  useEffect(
    () => () => {
      pendingActions.cancel();
      notifications.hide(BATCH_DELETE_TOAST_ID);
    },
    [pendingActions],
  );

  const visibleRows = rows.filter((row) => !pendingDeleteIds.has(getRowId(row)));

  const removeFromPending = (batchRows: TRow[]) => {
    setPendingDeleteIds((previous) => {
      const next = new Set(previous);
      batchRows.forEach((row) => next.delete(getRowId(row)));
      return next;
    });
  };

  const deleteRow = (row: TRow) => {
    setPendingDeleteIds((previous) => new Set(previous).add(getRowId(row)));

    const isFirstInBatch = !batchRowsRef.current;
    const batchRows = [...(batchRowsRef.current ?? []), row];
    batchRowsRef.current = batchRows;
    pendingActions.cancel();

    const onCancel = () => {
      pendingActions.cancel();
      batchRowsRef.current = null;
      notifications.hide(BATCH_DELETE_TOAST_ID);
      removeFromPending(batchRows);
      showV2CompactToast({ message: getUndoToastMessage(batchRows.length) });
    };

    pendingActions.schedule(batchRows, AUTO_CLOSE_TOAST_DURATION, (rowsToCommit) => {
      batchRowsRef.current = null;
      notifications.hide(BATCH_DELETE_TOAST_ID);
      if (rowsToCommit.length > 1) {
        onConfirmBulkDelete(rowsToCommit);
      } else {
        rowsToCommit.forEach(onConfirmDelete);
      }
      removeFromPending(rowsToCommit);
    });

    const message = getDeleteToastMessage(batchRows.length);
    if (isFirstInBatch) {
      showV2Toast({ id: BATCH_DELETE_TOAST_ID, message, autoClose: false, onCancel });
    } else {
      updateV2Toast({ id: BATCH_DELETE_TOAST_ID, message, autoClose: false, onCancel });
    }
  };

  const commitPendingRowDeletes = () => {
    pendingActions.commitNow((batchRows) => {
      batchRowsRef.current = null;
      notifications.hide(BATCH_DELETE_TOAST_ID);
      if (batchRows.length > 1) {
        onConfirmBulkDelete(batchRows);
      } else {
        batchRows.forEach(onConfirmDelete);
      }
      removeFromPending(batchRows);
    });
  };

  return { visibleRows, deleteRow, commitPendingRowDeletes };
};
